import { type FunctionComponent } from "react";
import { CheckOutlined, CheckSquareOutlined, DeleteOutlined } from "@ant-design/icons";
import { Button, Popover, Space, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { type ScheduledTeamMemberTaskAutomation } from "@fitness-app/data-models/entities/AutomationTemplate";
import { ProgramAutomationTaskStatus } from "@fitness-app/data-models/entities/ProgramAutomation";
import {
  type TeamMemberTaskCategory,
  type TeamMemberTaskPriority,
} from "@fitness-app/data-models/entities/TeamMemberTask";

import { getMinutesWithLeadingZero } from "~/modules/Automation/utils/getMinutesWithLeadingZero";

const priorityLabels: Record<TeamMemberTaskPriority, string> = {
  low: "Niski",
  medium: "Średni",
  high: "Wysoki",
  urgent: "Pilny",
};

const priorityColors: Record<TeamMemberTaskPriority, string> = {
  low: "#9CA3AF",
  medium: "#3B82F6",
  high: "#F97316",
  urgent: "#EF4444",
};

const categoryLabels: Record<TeamMemberTaskCategory, string> = {
  training_plan: "Plan treningowy",
  nutrition_plan: "Plan żywieniowy",
  client_communication: "Komunikacja z klientem",
  measurements: "Pomiary",
  progress_review: "Przegląd postępów",
  program_renewals: "Odnowienia programu",
  other: "Inne",
};

interface OwnProps {
  item: ScheduledTeamMemberTaskAutomation;
  onRemove: () => void;
  status?: ProgramAutomationTaskStatus;
}

type Props = OwnProps;

const TeamMemberTaskItem: FunctionComponent<Props> = ({ item, onRemove, status }) => {
  const { t } = useTranslation(["automation", "common"]);
  const minutesWithLeadingZero = getMinutesWithLeadingZero(item.sentTime.minutes);

  return (
    <Popover
      title={t("automation:programAutomation.teamMemberTask")}
      content={
        <Space direction="vertical" className="min-w-[200px]">
          <Space>
            <Typography.Text type="secondary">{t("automation:teamMemberTaskForm.title")}:</Typography.Text>
            <Typography.Text>{item.title}</Typography.Text>
          </Space>
          {item.description && (
            <Space>
              <Typography.Text type="secondary">{t("automation:teamMemberTaskForm.description")}:</Typography.Text>
              <Typography.Text>{item.description}</Typography.Text>
            </Space>
          )}
          <Space>
            <Typography.Text type="secondary">{t("automation:teamMemberTaskForm.priority")}:</Typography.Text>
            <Tag color={priorityColors[item.priority]}>{priorityLabels[item.priority]}</Tag>
          </Space>
          <Space>
            <Typography.Text type="secondary">{t("automation:teamMemberTaskForm.category")}:</Typography.Text>
            <Typography.Text>{categoryLabels[item.category]}</Typography.Text>
          </Space>
          <Space>
            <Typography.Text type="secondary">{t("automation:programAutomation.operationTime")}:</Typography.Text>
            <Typography.Text>
              {item.sentTime.hours}:{minutesWithLeadingZero}
            </Typography.Text>
          </Space>
          <Button danger icon={<DeleteOutlined />} type="text" onClick={onRemove} />
        </Space>
      }
    >
      <CheckSquareOutlined className="mr-2" />
      <span>
        {item.sentTime.hours}:{minutesWithLeadingZero}
      </span>

      {status === ProgramAutomationTaskStatus.Done && <CheckOutlined className="ml-2 text-green-500" />}
    </Popover>
  );
};

export default TeamMemberTaskItem;
